/**
 * application-form — pure form-structure logic (application-form-builder spec,
 * task 2.1).
 *
 * This module holds the I/O-free rules that govern an Application_Form's
 * *structure*: page/field title-label validation, append + reorder display
 * ordering, the supported Field_Type set, and the per-Division visibility
 * conjunction of a page and its field. It mirrors the established "pure core,
 * thin adapter" precedents (`division-registry/visibility.ts`,
 * `division-registry/registryLogic.ts`, `person-service/personLogic.ts`): no
 * AWS SDK, no I/O, so the exact same code is exercised by the Lambda (`jest`)
 * and re-used by the frontend (`vitest`).
 *
 * The per-Division half of visibility is delegated to the existing
 * `division-registry/visibility.ts#isVisibleForDivision` for BOTH the page and
 * the field, then conjoined (Req 4.4, 4.5). The audience half of
 * Effective_Visibility lives in the separate `audienceVisibility.ts` module.
 *
 * Requirements: 1.1, 1.3, 1.5, 1.6, 2.1, 2.2, 2.5, 2.6, 4.2, 4.3, 4.4, 4.5
 */

import { FieldType } from '../../types/database';
import type { Division } from '../division-registry/registryLogic';
import { isVisibleForDivision } from '../division-registry/visibility';

// ---------------------------------------------------------------------------
// Shared validation-error shape
//
// NOTE: The canonical `FieldValidationError` is owned by `fieldValidation.ts`
// (task 1.1). It is mirrored here so this module compiles and is testable on
// its own as a wave-0 sibling task; the two definitions are structurally
// identical and intended to be consolidated onto the `fieldValidation.ts`
// export once both modules have landed.
// ---------------------------------------------------------------------------

/** Every validation-error code used across the application-form pure modules. */
export type FieldValidationErrorCode =
  | 'REQUIRED'
  | 'WRONG_TYPE'
  | 'WORD_LIMIT'
  | 'CHAR_LIMIT'
  | 'EMPTY_OPTIONS'
  | 'NOT_AN_OPTION'
  | 'FILE_TYPE'
  | 'FILE_SIZE'
  | 'FILE_COUNT'
  | 'BAD_EMAIL'
  | 'BAD_PHONE'
  | 'BAD_NUMBER'
  | 'BAD_DATE'
  | 'BOUND_VALUE_NOT_ALLOWED'
  | 'INACTIVE_DIVISION'
  | 'MISSING_REQUIRED_DOCUMENT'
  | 'CONTACT_FIELD_MISSING';

/**
 * A single, field-identifying validation error. `fieldId` names the offending
 * input (`'title'`, `'label'`, `'fieldType'`, or a real Form_Field id) so a
 * Director sees exactly which element to fix (Req 1.6, 2.5, 2.6).
 */
export interface FieldValidationError {
  fieldId: string;
  code: FieldValidationErrorCode;
  message: string;
}

// ---------------------------------------------------------------------------
// Visibility-bearing structural shapes
//
// Minimal read-models for the per-Division visibility decision. A Form_Page and
// a Form_Field each carry a nullable `visibleDivisionKeys` selection with the
// same semantics as the wire field added in the division-registry feature
// (null/empty ⇒ all active divisions).
// ---------------------------------------------------------------------------

/** The page half of the visibility conjunction. */
export interface PageVis {
  visibleDivisionKeys: readonly string[] | null;
}

/** The field half of the visibility conjunction. */
export interface FieldVis {
  visibleDivisionKeys: readonly string[] | null;
}

// ---------------------------------------------------------------------------
// Title / label validation (Req 1.6, 2.5)
// ---------------------------------------------------------------------------

/** Whether a string is null/undefined or contains only whitespace. */
function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

/**
 * Validate a Form_Page title. An empty or whitespace-only title is rejected
 * with an error identifying the `title` field; otherwise `null` (Req 1.6).
 */
export function validatePageTitle(title: string): FieldValidationError | null {
  if (isBlank(title)) {
    return {
      fieldId: 'title',
      code: 'REQUIRED',
      message: 'Page title is required and must not be empty or whitespace.',
    };
  }
  return null;
}

/**
 * Validate a Form_Field label. An empty or whitespace-only label is rejected
 * with an error identifying the `label` field; otherwise `null` (Req 2.5).
 */
export function validateFieldLabel(label: string): FieldValidationError | null {
  if (isBlank(label)) {
    return {
      fieldId: 'label',
      code: 'REQUIRED',
      message: 'Field label is required and must not be empty or whitespace.',
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Display ordering (Req 1.1, 1.3, 2.1, 2.2)
// ---------------------------------------------------------------------------

/**
 * The display order for a newly appended page or field: one past the current
 * maximum so the new element sorts strictly after every existing one (Req 1.1,
 * 2.1). An empty collection yields `0`, matching the dense, 0-based sequence
 * produced by {@link normalizeDisplayOrder}.
 */
export function nextDisplayOrder(existing: ReadonlyArray<{ displayOrder: number }>): number {
  let max = -1;
  for (const item of existing) {
    if (item.displayOrder > max) {
      max = item.displayOrder;
    }
  }
  return max + 1;
}

/**
 * Normalize a requested reorder into a dense, duplicate-free `0..n-1` sequence
 * (Req 1.3, 2.2). Each `id` in `orderedIds` that names a real item is assigned
 * the next ordinal in the requested sequence (duplicates ignored after the
 * first occurrence); any items absent from `orderedIds` are appended afterwards
 * in their existing relative order so the result is total and covers every
 * item exactly once.
 *
 * @param orderedIds the ids in the Director's requested order.
 * @param items      the items being ordered; only their `id` is consulted.
 * @returns a map from item id to its dense display order.
 */
export function normalizeDisplayOrder<T extends { id: string }>(
  orderedIds: readonly string[],
  items: readonly T[],
): Map<string, number> {
  const itemIds = new Set(items.map((item) => item.id));
  const order = new Map<string, number>();
  let next = 0;

  for (const id of orderedIds) {
    if (itemIds.has(id) && !order.has(id)) {
      order.set(id, next);
      next += 1;
    }
  }

  // Items not named in the requested order keep their existing relative order,
  // appended after the requested ones so the sequence stays dense and total.
  for (const item of items) {
    if (!order.has(item.id)) {
      order.set(item.id, next);
      next += 1;
    }
  }

  return order;
}

// ---------------------------------------------------------------------------
// Per-Division visibility conjunction (Req 4.2, 4.3, 4.4, 4.5)
// ---------------------------------------------------------------------------

/**
 * Whether a Form_Field is visible for a given Division: the field is shown only
 * when BOTH its containing Form_Page AND the Form_Field itself are visible for
 * that Division (Req 4.5). Each half delegates to the shared
 * `isVisibleForDivision` helper, which applies the null/empty ⇒ all-active
 * semantics (Req 4.2) and the populated-list scoping (Req 4.3). A page that
 * excludes the Division hides every field on it regardless of the field's own
 * scope (Req 4.4, 4.5).
 *
 * @param page            the containing page's visibility selection.
 * @param field           the field's own visibility selection.
 * @param divisionKey     the Division key being tested.
 * @param activeDivisions the competition's Divisions; only `active` ones count
 *                        for the all-divisions case.
 */
export function isFieldVisibleForDivision(
  page: PageVis,
  field: FieldVis,
  divisionKey: string,
  activeDivisions: readonly Division[],
): boolean {
  const pageVisible = isVisibleForDivision(page.visibleDivisionKeys, divisionKey, activeDivisions);
  const fieldVisible = isVisibleForDivision(field.visibleDivisionKeys, divisionKey, activeDivisions);
  return pageVisible && fieldVisible;
}

// ---------------------------------------------------------------------------
// Field-type validation (Req 2.6)
// ---------------------------------------------------------------------------

/** The supported Field_Type set, derived from the canonical `FieldType` enum. */
export const SUPPORTED_FIELD_TYPES: ReadonlySet<string> = new Set<string>(Object.values(FieldType));

/**
 * Validate a Custom_Field's Field_Type. A value outside the supported
 * `FieldType` set is rejected with an error identifying the `fieldType` field;
 * a supported value yields `null` (Req 2.6).
 */
export function validateFieldType(fieldType: string): FieldValidationError | null {
  if (!SUPPORTED_FIELD_TYPES.has(fieldType)) {
    return {
      fieldId: 'fieldType',
      code: 'WRONG_TYPE',
      message:
        `Unsupported field type "${String(fieldType)}"; expected one of ` +
        `${[...SUPPORTED_FIELD_TYPES].join(' | ')}.`,
    };
  }
  return null;
}
